import React, { useState } from 'react';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  const h = { Accept: 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
};

export const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pl-PL'); } catch { return d; }
};
export const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('pl-PL'); } catch { return d; }
};

export const Card = ({ label, value, sub, bg, color }) => (
  <div style={{
    background: bg || '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
    padding: '14px 20px', flex: 1, minWidth: 110,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  }}>
    <div style={{ fontSize: '1.8em', fontWeight: 700, color: color || '#333', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: '0.72em', color: '#666', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    {sub != null && <div style={{ fontSize: '0.75em', color: '#aaa', marginTop: 2 }}>{sub}</div>}
  </div>
);

export const StackedBarChart = ({ data, keys, colors, labels }) => {
  const [tooltip, setTooltip] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const W = 820, H = 220;
  const PAD = { top: 16, right: 16, bottom: 36, left: 40 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  if (!data.length) return <div style={{ textAlign: 'center', color: '#bbb', padding: 32 }}>Brak danych</div>;

  const visibleKeys = keys.filter((k) => !hidden.has(k));
  const maxVal = Math.max(1, ...data.map((d) => visibleKeys.reduce((s, k) => s + (d[k] || 0), 0)));
  const barW = Math.max(4, Math.min(32, chartW / data.length - 3));
  const xStep = chartW / data.length;
  const yTicks = [0, 25, 50, 75, 100].map((p) => Math.round((p / 100) * maxVal));

  const toggleKey = (k) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 8, fontSize: '0.78em', flexWrap: 'wrap' }}>
        {keys.map((k, i) => {
          const isHidden = hidden.has(k);
          return (
            <span
              key={k}
              onClick={() => toggleKey(k)}
              title={isHidden ? 'Kliknij, aby pokazać' : 'Kliknij, aby ukryć'}
              style={{ cursor: 'pointer', opacity: isHidden ? 0.35 : 1, userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <span style={{
                display: 'inline-block', width: 10, height: 10,
                background: isHidden ? '#ccc' : colors[i],
                borderRadius: 2, verticalAlign: 'middle',
              }} />
              <span style={{ textDecoration: isHidden ? 'line-through' : 'none' }}>{labels[i]}</span>
            </span>
          );
        })}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {yTicks.map((t) => {
          const y = PAD.top + chartH - (t / maxVal) * chartH;
          return (
            <g key={t}>
              <line x1={PAD.left} y1={y} x2={PAD.left + chartW} y2={y} stroke="#f0f0f0" />
              <text x={PAD.left - 5} y={y + 3} textAnchor="end" fontSize={9} fill="#bbb">{t}</text>
            </g>
          );
        })}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="#e0e0e0" />
        <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="#e0e0e0" />
        {data.map((d, i) => {
          const x = PAD.left + i * xStep + (xStep - barW) / 2;
          let yOffset = PAD.top + chartH;
          return (
            <g key={i}
              onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, d, i })}
              onMouseLeave={() => setTooltip(null)}
              style={{ cursor: 'pointer' }}
            >
              {keys.map((k, ki) => {
                if (hidden.has(k)) return null;
                const val = d[k] || 0;
                const h = (val / maxVal) * chartH;
                yOffset -= h;
                return h > 0 ? (
                  <rect key={k} x={x} y={yOffset} width={barW} height={h}
                    fill={colors[ki]} rx={ki === keys.length - 1 ? 2 : 0} />
                ) : null;
              })}
              {i % Math.max(1, Math.ceil(data.length / 12)) === 0 && (
                <text x={x + barW / 2} y={PAD.top + chartH + 14} textAnchor="middle" fontSize={8} fill="#bbb">
                  {d.date?.slice(5)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 60, zIndex: 9999,
          background: '#fff', border: '1px solid #ddd', borderRadius: 6,
          padding: '8px 12px', fontSize: '0.8em', pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{fmtDate(tooltip.d.date)}</div>
          {keys.map((k, ki) => (
            <div key={k} style={{ color: hidden.has(k) ? '#ccc' : colors[ki] }}>
              {labels[ki]}: <strong>{tooltip.d[k] || 0}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StepTable = ({ steps }) => {
  if (!steps.length) return <div style={{ color: '#bbb', fontSize: '0.85em' }}>Brak danych</div>;
  const maxDone = Math.max(1, ...steps.map((s) => s.done || 0));
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82em' }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #eee' }}>
          <th style={{ textAlign: 'left', padding: '5px 8px', color: '#888', fontWeight: 600 }}>Krok</th>
          <th style={{ textAlign: 'center', padding: '5px 8px', color: '#2e7d32', fontWeight: 600 }}>Ukończone</th>
          <th style={{ textAlign: 'center', padding: '5px 8px', color: '#c62828', fontWeight: 600 }}>Błędy</th>
          <th style={{ textAlign: 'center', padding: '5px 8px', color: '#f57f17', fontWeight: 600 }}>Oczekujące</th>
          <th style={{ padding: '5px 8px', width: 120 }}></th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s) => {
          const total = (s.done || 0) + (s.failed || 0) + (s.waiting || 0);
          const failPct = total > 0 ? ((s.failed || 0) / total * 100).toFixed(0) : 0;
          return (
            <tr key={s.block_name} style={{ borderBottom: '1px solid #f5f5f5' }}>
              <td style={{ padding: '6px 8px', fontWeight: 500 }}>{s.block_name}</td>
              <td style={{ textAlign: 'center', padding: '6px 8px', color: '#2e7d32', fontWeight: 600 }}>{s.done || 0}</td>
              <td style={{ textAlign: 'center', padding: '6px 8px', color: s.failed ? '#c62828' : '#bbb', fontWeight: s.failed ? 700 : 400 }}>
                {s.failed || 0}
                {s.failed > 0 && <span style={{ fontSize: '0.8em', marginLeft: 3 }}>({failPct}%)</span>}
              </td>
              <td style={{ textAlign: 'center', padding: '6px 8px', color: s.waiting ? '#f57f17' : '#bbb' }}>{s.waiting || 0}</td>
              <td style={{ padding: '6px 8px' }}>
                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round((s.done || 0) / maxDone * 100)}%`, background: '#2e7d32', borderRadius: 3 }} />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const ErrorsList = ({ errors, type }) => {
  if (!errors.length) return <div style={{ color: '#bbb', fontSize: '0.85em', padding: '8px 0' }}>Brak błędów</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {errors.map((e) => (
        <div key={e.id} style={{
          background: '#fff8f8', border: '1px solid #ffcdd2', borderRadius: 6,
          padding: '10px 14px', fontSize: '0.82em',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 4 }}>
            <span>
              {type === 'tender' ? (
                <a href={`#/tenders/${e.tender_id}/show`} style={{ color: '#c62828', fontWeight: 600, textDecoration: 'none' }}>
                  {e['tender__title']?.slice(0, 60) || `Przetarg #${e.tender_id}`}
                  {e['tender__title']?.length > 60 ? '…' : ''}
                </a>
              ) : (
                <span>
                  <a href={`#/tenders/${e['bidder_offer__tender_id']}/show`} style={{ color: '#c62828', fontWeight: 600, textDecoration: 'none' }}>
                    {e['bidder_offer__tender__title']?.slice(0, 40) || `Przetarg #${e['bidder_offer__tender_id']}`}
                    {e['bidder_offer__tender__title']?.length > 40 ? '…' : ''}
                  </a>
                  <span style={{ color: '#888', marginLeft: 6 }}>→ {e['bidder_offer__bidder__name']?.slice(0, 30) || '?'}</span>
                </span>
              )}
            </span>
            <span style={{ color: '#aaa', whiteSpace: 'nowrap' }}>{fmtDateTime(e.updated_at)}</span>
          </div>
          <div style={{ color: '#555', marginBottom: 2 }}>
            <span style={{ background: '#fce4ec', color: '#880e4f', borderRadius: 10, padding: '1px 7px', fontSize: '0.85em', marginRight: 6 }}>
              {e.current_block}
            </span>
          </div>
          <div style={{ color: '#777', fontStyle: 'italic', marginTop: 4, wordBreak: 'break-word' }}>
            {e.error_message?.slice(0, 200)}{e.error_message?.length > 200 ? '…' : ''}
          </div>
        </div>
      ))}
    </div>
  );
};

export const Section = ({ title, children, action }) => (
  <div style={{
    background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
    padding: '18px 22px', marginBottom: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: '0.95em', color: '#333', fontWeight: 700 }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

export const STEP_PALETTE = [
  '#e53935','#8e24aa','#1e88e5','#00897b','#f4511e',
  '#6d4c41','#546e7a','#c0ca33','#fb8c00','#00acc1',
  '#5e35b1','#43a047','#d81b60','#1565c0','#558b2f',
];

// Palette for multi-line chart — enough colors for ~15 steps
const LINE_PALETTE = [
  '#e53935','#8e24aa','#1e88e5','#00897b','#f4511e',
  '#6d4c41','#546e7a','#c0ca33','#fb8c00','#00acc1',
  '#5e35b1','#43a047','#d81b60','#1565c0','#558b2f',
];

export const MultiLineChart = ({ stepErrors }) => {
  const { dates = [], blocks = [], data = {} } = stepErrors || {};
  const [hidden, setHidden] = useState(new Set());
  const [tooltip, setTooltip] = useState(null);

  const W = 820, H = 240;
  const PAD = { top: 16, right: 16, bottom: 36, left: 44 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const toggleBlock = (b) => setHidden((prev) => {
    const next = new Set(prev);
    next.has(b) ? next.delete(b) : next.add(b);
    return next;
  });

  if (!dates.length || !blocks.length)
    return <div style={{ textAlign: 'center', color: '#bbb', padding: 32 }}>Brak danych</div>;

  const visibleBlocks = blocks.filter((b) => !hidden.has(b));
  const maxVal = Math.max(1, ...dates.flatMap((d) =>
    visibleBlocks.map((b) => data[d]?.[b] || 0)
  ));

  const xStep = chartW / Math.max(dates.length - 1, 1);
  const yOf = (v) => PAD.top + chartH - (v / maxVal) * chartH;
  const xOf = (i) => PAD.left + i * xStep;

  const yTicks = [0, 25, 50, 75, 100].map((p) => Math.round((p / 100) * maxVal));

  return (
    <div style={{ position: 'relative' }}>
      {/* Legend — wrap, toggleable */}
      <div style={{ display: 'flex', gap: '6px 14px', marginBottom: 10, fontSize: '0.75em', flexWrap: 'wrap' }}>
        {blocks.map((b, i) => {
          const isHidden = hidden.has(b);
          const color = LINE_PALETTE[i % LINE_PALETTE.length];
          return (
            <span
              key={b}
              onClick={() => toggleBlock(b)}
              title={isHidden ? 'Kliknij, aby pokazać' : 'Kliknij, aby ukryć'}
              style={{ cursor: 'pointer', opacity: isHidden ? 0.3 : 1, userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <span style={{ display: 'inline-block', width: 18, height: 3, background: isHidden ? '#ccc' : color, borderRadius: 2 }} />
              <span style={{ textDecoration: isHidden ? 'line-through' : 'none', color: '#444' }}>{b}</span>
            </span>
          );
        })}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* Gridlines + Y labels */}
        {yTicks.map((t) => {
          const y = yOf(t);
          return (
            <g key={t}>
              <line x1={PAD.left} y1={y} x2={PAD.left + chartW} y2={y} stroke="#f0f0f0" />
              <text x={PAD.left - 5} y={y + 3} textAnchor="end" fontSize={9} fill="#bbb">{t}</text>
            </g>
          );
        })}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="#e0e0e0" />
        <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="#e0e0e0" />

        {/* X labels */}
        {dates.map((d, i) => (
          i % Math.max(1, Math.ceil(dates.length / 12)) === 0 && (
            <text key={d} x={xOf(i)} y={PAD.top + chartH + 14} textAnchor="middle" fontSize={8} fill="#bbb">
              {d.slice(5)}
            </text>
          )
        ))}

        {/* Lines */}
        {blocks.map((b, bi) => {
          if (hidden.has(b)) return null;
          const color = LINE_PALETTE[bi % LINE_PALETTE.length];
          const points = dates.map((d, i) => `${xOf(i)},${yOf(data[d]?.[b] || 0)}`).join(' ');
          return (
            <polyline key={b} points={points} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
          );
        })}

        {/* Invisible hover rects per date */}
        {dates.map((d, i) => (
          <rect
            key={d}
            x={xOf(i) - xStep / 2}
            y={PAD.top}
            width={xStep}
            height={chartH}
            fill="transparent"
            onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, d, i })}
            onMouseLeave={() => setTooltip(null)}
          />
        ))}

        {/* Dots on hover */}
        {tooltip && blocks.map((b, bi) => {
          if (hidden.has(b)) return null;
          const val = data[tooltip.d]?.[b] || 0;
          if (!val) return null;
          const color = LINE_PALETTE[bi % LINE_PALETTE.length];
          return <circle key={b} cx={xOf(tooltip.i)} cy={yOf(val)} r={3} fill={color} />;
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 14, top: tooltip.y - 80, zIndex: 9999,
          background: '#fff', border: '1px solid #ddd', borderRadius: 6,
          padding: '8px 12px', fontSize: '0.78em', pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6, borderBottom: '1px solid #f0f0f0', paddingBottom: 4 }}>
            {fmtDate(tooltip.d)}
          </div>
          {blocks
            .filter((b) => !hidden.has(b) && (data[tooltip.d]?.[b] || 0) > 0)
            .sort((a, b) => (data[tooltip.d]?.[b] || 0) - (data[tooltip.d]?.[a] || 0))
            .map((b, bi) => (
              <div key={b} style={{ color: LINE_PALETTE[blocks.indexOf(b) % LINE_PALETTE.length], display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <span>{b}</span>
                <strong>{data[tooltip.d]?.[b] || 0}</strong>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

export const STACK_KEYS   = ['done', 'failed', 'waiting', 'running'];
export const STACK_COLORS = ['#43a047', '#e53935', '#fb8c00', '#1e88e5'];
export const STACK_LABELS = ['Ukończone', 'Błędy', 'Oczekujące', 'W trakcie'];

export const DaysSelector = ({ days, setDays }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{ fontSize: '0.8em', color: '#aaa', marginRight: 4 }}>Zakres:</span>
    {[7, 14, 30, 60].map((d) => (
      <button
        key={d}
        onClick={() => setDays(d)}
        style={{
          background: days === d ? '#c62828' : '#f5f5f5',
          color: days === d ? '#fff' : '#555',
          border: 'none', borderRadius: 6, padding: '4px 12px',
          cursor: 'pointer', fontSize: '0.8em', fontWeight: 600, marginLeft: 4,
        }}
      >
        {d}d
      </button>
    ))}
  </div>
);
